import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { Language, messages } from './messages';

const LANG_STORAGE_KEY = 'cooperaid.language';

interface TranslateParams {
  [key: string]: string | number;
}

interface I18nContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, params?: TranslateParams) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const getNestedValue = (obj: any, key: string): string | undefined => {
  return key.split('.').reduce((acc: any, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
};

const interpolate = (value: string, params?: TranslateParams): string => {
  if (!params) {
    return value;
  }
  return value.replace(/\{(\w+)\}/g, (_match, token) => {
    const replacement = params[token];
    return replacement === undefined ? `{${token}}` : String(replacement);
  });
};

const detectInitialLanguage = (): Language => {
  const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
  if (stored === 'en' || stored === 'de') {
    return stored;
  }

  return 'de';
};

export const I18nProvider = ({ children }: PropsWithChildren) => {
  const [language, setLanguage] = useState<Language>(detectInitialLanguage);

  useEffect(() => {
    window.localStorage.setItem(LANG_STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo<I18nContextValue>(() => {
    const t = (key: string, params?: TranslateParams): string => {
      const text = getNestedValue(messages[language], key) ?? getNestedValue(messages.de, key) ?? key;
      return interpolate(String(text), params);
    };

    return {
      language,
      setLanguage,
      t,
    };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextValue => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};
